import { useCallback, useEffect, useRef, useState } from "react";
import getBlobDuration from "get-blob-duration";
import "./AudioPlayer.css";

const AudioPlayer = (props: { audioTrack: string }) => {
  const { audioTrack } = props;
  const [timeProgress, setTimeProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const progressBarRef = useRef<any>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const playAnimationRef = useRef<number | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const blobDurationRef = useRef<number | null>(null);

  const togglePlayPause = () => {
    setIsPlaying((prev) => !prev);
  };

  useEffect(() => {
    (async () => {
      try {
        const blobDuration = await getBlobDuration(audioTrack);
        blobDurationRef.current = blobDuration;
        setDuration(blobDuration);
        onLoadedMetadata(blobDuration);
      } catch (err: unknown) {
        console.log(err);
      }
    })();
    return () => {
      setDuration(0);
    };
  }, [audioTrack]);

  const handleDownloadAudio = () => {
    const a = document.createElement("a");
    a.href = audioTrack;
    a.download = "my-audio.mp3";
    a.click();
    a.remove();
  };

  const repeat = useCallback(() => {
    const currentTime = audioRef?.current?.currentTime as number;
    setTimeProgress(currentTime);
    progressBarRef.current.value = currentTime;
    progressBarRef.current.style.setProperty(
      "--range-progress",
      `${(progressBarRef.current.value / Math.round(duration)) * 100}%`
    );

    playAnimationRef.current = requestAnimationFrame(repeat);
  }, [audioRef, duration, progressBarRef, setTimeProgress]);

  useEffect(() => {
    if (audioRef.current?.currentTime && audioRef.current?.currentTime > 0) {
      if (audioRef.current?.currentTime == duration) {
        progressBarRef.current.style.setProperty("--range-progress", "0%");
        progressBarRef.current.value = 0;
        setIsPlaying((prev) => !prev);
        setTimeProgress(0);
      }
    }
  }, [audioRef.current?.currentTime]);

  useEffect(() => {
    if (isPlaying) {
      audioRef.current?.play();
    } else {
      if (audioRef.current?.currentTime === duration) return;
      audioRef.current?.pause();
    }
    playAnimationRef.current = requestAnimationFrame(repeat);

    return () => {
      cancelAnimationFrame(playAnimationRef.current as number);
    };
  }, [isPlaying, audioRef, repeat]);

  const handleProgressChange = () => {
    if (audioRef.current !== null) {
      audioRef.current.currentTime = progressBarRef.current.value;
      progressBarRef.current.style.setProperty(
        "--range-progress",
        `${(progressBarRef.current.value / Math.round(duration)) * 100}%`
      );
    }
  };

  const onLoadedMetadata = (blobDuration: number) => {
    progressBarRef.current.max = Math.round(blobDuration);
  };

  const formatTime = (time: number) => {
    if (time && !isNaN(time)) {
      const minutes = Math.round(time / 60);
      const formatMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
      const seconds = Math.round(time % 60);
      const formatSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
      return `${formatMinutes}:${formatSeconds}`;
    }
    return "00:00";
  };

  return (
    <div className="audio-player-container w-full mt-4">
      <div className="hidden">
        <audio
          src={audioTrack}
          ref={audioRef}
          onLoadedMetadata={() =>
            onLoadedMetadata(blobDurationRef.current as number)
          }
        />
      </div>
      <div className="flex justify-center items-center">
        <div
          className="audio-controls sm:w-10 sm:h-10 w-6 h-6 bg-slate-400 flex justify-center rounded-full items-center cursor-pointer"
          onClick={togglePlayPause}
          tabIndex={0}
          aria-label="Click here to play"
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter" || event.code == "Space") {
              togglePlayPause();
            }
          }}
        >
          {isPlaying ? (
            <i className="fa-solid fa-pause text-[10px] sm:text-[14px]"></i>
          ) : (
            <i className="fa-solid fa-play  text-[10px] sm:text-[14px]"></i>
          )}
        </div>
        <div className="audio-progressbar flex justify-center items-center grow ml-4">
          <div className="time current w-12 mr-1">
            {formatTime(timeProgress)}
          </div>
          <div className="grow">
            <input
              aria-label="audio player"
              type="range"
              ref={progressBarRef}
              onChange={handleProgressChange}
              defaultValue="0"
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
                  handleProgressChange();
                }
              }}
            />
          </div>
          <div className="time ml-2">{formatTime(Math.round(duration))}</div>
        </div>
        <div
          className="audio-download ml-4 cursor-pointer"
          onClick={handleDownloadAudio}
          tabIndex={0}
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter") {
              handleDownloadAudio();
            }
          }}
          aria-label="click here to download"
        >
          <i className="fa-solid fa-download text-[10px] sm:text-[14px]"></i>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
